/**
 * The customer profile: the record the owner acts from.
 *
 * Layout follows the mockup: header with segment and masked contact, an action bar, then
 * summary tiles, orders, the Research Rewards ledger, support and referrals, notes and tags,
 * and the communications timeline.
 *
 * Two role rules are visible in the markup rather than buried in a helper. An agent has no
 * money on this page at all, because the API never sent any; and every remedy button is
 * labelled Request for an agent and Issue for the owner, which is the two person control
 * this shop otherwise does not have.
 */
import { useState } from 'react';
import type { Role } from '@elutide/shared';
import {
  AgeText,
  Card,
  ConfirmDialog,
  MoneyText,
  Pill,
  Skeleton,
  buttonStyle,
} from './temp-ui';
import { RevealField } from './RevealField';
import { SendMessageDialog } from './SendMessageDialog';
import {
  useAddNote,
  useAddTag,
  useCustomer,
  useCustomerCommunications,
  useCustomerOrders,
  useCustomerRewards,
  useIssueCredit,
  useRemoveTag,
  useRequestCredit,
} from './api';
import { hasFullContact } from './types';

export function CustomerProfile({ customerKey, role }: { customerKey: string; role: Role }) {
  const profile = useCustomer(customerKey);
  const isOwner = role === 'owner';
  const orders = useCustomerOrders(customerKey);
  const rewards = useCustomerRewards(customerKey, isOwner);
  const communications = useCustomerCommunications(customerKey, isOwner);
  const addNote = useAddNote(customerKey);
  const addTag = useAddTag(customerKey);
  const removeTag = useRemoveTag(customerKey);
  const issueCredit = useIssueCredit(customerKey);
  const requestCredit = useRequestCredit(customerKey);

  const [creditOpen, setCreditOpen] = useState(false);
  const [messageOpen, setMessageOpen] = useState<null | 'email' | 'sms'>(null);
  const [creditCents, setCreditCents] = useState(2_500);

  if (profile.isLoading) return <Skeleton shape="text" lines={10} />;
  if (profile.isError || !profile.data) {
    return <Card title="Customer">That customer could not be loaded.</Card>;
  }

  const customer = profile.data;
  const money = hasFullContact(customer);

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <Card>
        <header style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap' }}>
          <h1 style={{ margin: 0, fontSize: 'var(--type-heading-2)' }}>{customer.display_name}</h1>
          <Pill tone="info">{customer.segment.replace('_', ' ')}</Pill>
          {customer.first_paid_at ? <Pill>since {customer.first_paid_at.slice(0, 7)}</Pill> : null}
          {customer.duplicate_flags.length > 0 ? (
            <Pill tone="warn">possible duplicate</Pill>
          ) : null}
        </header>

        <p style={{ color: 'var(--text-muted)', fontSize: 'var(--type-meta)' }}>{customer.segment_reason}</p>

        <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap', alignItems: 'center' }}>
          <RevealField
            customerKey={customerKey}
            field="email"
            label="Email"
            masked={customer.email_masked ?? customer.email ?? null}
          />
          <RevealField
            customerKey={customerKey}
            field="phone"
            label="Phone"
            masked={customer.phone_last4 ? `(***) ***-${customer.phone_last4}` : (customer.phone ?? null)}
          />
          {customer.shipping ? (
            <span style={{ color: 'var(--text-muted)' }}>
              {customer.shipping.city}, {customer.shipping.state}
            </span>
          ) : null}
        </div>

        <nav aria-label="Customer actions" style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-3)', flexWrap: 'wrap' }}>
          <button type="button" style={buttonStyle()} onClick={() => setMessageOpen('email')}>
            Email
          </button>
          <button type="button" style={buttonStyle()} onClick={() => setMessageOpen('sms')}>
            SMS via OpenPhone
          </button>
          <button type="button" style={buttonStyle(isOwner ? 'primary' : 'ghost')} onClick={() => setCreditOpen(true)}>
            {isOwner ? 'Issue credit' : 'Request credit'}
          </button>
          {/*
            Comp order and New order are owner actions served by the orders module
            (POST /orders/comp). They deep link rather than duplicating that flow here.
          */}
          {isOwner ? (
            <>
              <a href="/orders?comp=1" style={{ ...buttonStyle(), textDecoration: 'none' }}>
                Comp order
              </a>
              <a href="/orders?new=1" style={{ ...buttonStyle(), textDecoration: 'none' }}>
                New order
              </a>
            </>
          ) : null}
        </nav>
      </Card>

      {customer.duplicate_flags.length > 0 ? (
        <Card title="Possible duplicates">
          <ul style={{ margin: 0, paddingLeft: 'var(--space-4)' }}>
            {customer.duplicate_flags.map((flag) => (
              <li key={flag.customer_key}>
                <strong>{flag.display_name}</strong>
                <span style={{ color: 'var(--text-muted)' }}> · {flag.detail}</span>
              </li>
            ))}
          </ul>
          <p style={{ color: 'var(--text-faint)', fontSize: 'var(--type-meta)' }}>
            Flags only. Merging is not built, because merging is not reversible in WordPress.
          </p>
        </Card>
      ) : null}

      <div style={{ display: 'grid', gap: 'var(--space-4)', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
        <Card title="Orders">
          <p style={{ fontSize: 'var(--type-heading-3)', margin: 0 }}>{customer.orders_paid}</p>
        </Card>
        {money ? (
          <Card title="Lifetime">
            <p style={{ fontSize: 'var(--type-heading-3)', margin: 0 }}>
              <MoneyText cents={customer.lifetime_net_cents ?? null} />
            </p>
          </Card>
        ) : null}
        {money ? (
          <Card title="RR balance">
            <p style={{ fontSize: 'var(--type-heading-3)', margin: 0 }}>
              <MoneyText cents={customer.rr_balance_cents ?? null} tone="var(--copper)" />
            </p>
          </Card>
        ) : null}
        <Card title="Cadence">
          <p style={{ fontSize: 'var(--type-heading-3)', margin: 0 }}>
            {customer.reorder_cadence_days === null ? 'not enough orders' : `${Math.round(customer.reorder_cadence_days)}d`}
          </p>
        </Card>
      </div>

      <Card title="Orders">
        {orders.isLoading ? (
          <Skeleton shape="text" lines={4} />
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {(orders.data?.items ?? []).map((order) => (
              <li key={order.order_id} style={{ padding: 'var(--space-2) 0', borderBottom: 'var(--border-width) solid var(--border)' }}>
                <span data-mono="true">#{order.order_number}</span>
                <span style={{ color: 'var(--text-muted)' }}> · {order.item_count} items</span>
                {money ? (
                  <>
                    <span style={{ color: 'var(--text-muted)' }}> · </span>
                    <MoneyText cents={order.total_cents} />
                  </>
                ) : null}
                <span style={{ marginLeft: 'var(--space-2)' }}>
                  <Pill tone={order.ops_state === 'delivered' ? 'ok' : 'neutral'}>{order.ops_state}</Pill>
                </span>
              </li>
            ))}
            {(orders.data?.items ?? []).length === 0 ? <li style={{ color: 'var(--text-faint)' }}>No orders yet.</li> : null}
          </ul>
        )}
      </Card>

      {isOwner ? (
        <Card title="Research Rewards ledger">
          {rewards.isLoading ? (
            <Skeleton shape="text" lines={4} />
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {(rewards.data?.items ?? []).map((entry) => (
                <li key={entry.entry_id} style={{ display: 'flex', justifyContent: 'space-between', padding: 'var(--space-1) 0' }}>
                  <span style={{ color: 'var(--text-muted)' }}>
                    {entry.at.slice(0, 10)} · {entry.kind.replace('_', ' ')}
                    {entry.reason ? ` · ${entry.reason}` : ''}
                  </span>
                  <MoneyText cents={entry.delta_cents} tone={entry.delta_cents >= 0 ? 'var(--ok)' : 'var(--crit)'} />
                </li>
              ))}
              {(rewards.data?.items ?? []).length === 0 ? (
                <li style={{ color: 'var(--text-faint)' }}>No reward entries.</li>
              ) : null}
            </ul>
          )}
        </Card>
      ) : null}

      <Card title="Support, referrals and claims">
        <p style={{ margin: 0 }}>
          Tickets:{' '}
          {customer.support_ticket_refs.length === 0
            ? 'none'
            : customer.support_ticket_refs.map((ref) => (
                <a key={ref} href={`/support/${ref}`} data-mono="true" style={{ marginRight: 'var(--space-2)' }}>
                  {ref}
                </a>
              ))}
        </p>
      </Card>

      <Card title="Notes and tags">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            const form = event.currentTarget;
            const body = String(new FormData(form).get('body') ?? '');
            if (body.trim() === '') return;
            addNote.mutate({ body, pinned: false });
            form.reset();
          }}
          style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}
        >
          <input
            name="body"
            aria-label="New note"
            placeholder="Internal note. Never shown to the customer."
            style={{
              flex: 1,
              padding: 'var(--space-2)',
              borderRadius: 'var(--radius-input)',
              border: 'var(--border-width) solid var(--border)',
              background: 'var(--surface-2)',
              color: 'var(--text)',
            }}
          />
          <button type="submit" style={buttonStyle('primary')}>
            Add note
          </button>
        </form>

        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {customer.notes.map((note) => (
            <li key={note.note_id} style={{ padding: 'var(--space-2) 0' }}>
              <span className="caps-label" style={{ color: 'var(--text-faint)' }}>
                {note.author_name} · <AgeText at={note.created_at} /> ago
              </span>
              <p style={{ margin: 0 }}>{note.body}</p>
            </li>
          ))}
        </ul>

        <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', marginTop: 'var(--space-3)' }}>
          {customer.tags.map((tag) => (
            <button key={tag} type="button" style={buttonStyle()} onClick={() => removeTag.mutate(tag)}>
              {tag} ×
            </button>
          ))}
          <form
            onSubmit={(event) => {
              event.preventDefault();
              const form = event.currentTarget;
              const tag = String(new FormData(form).get('tag') ?? '');
              if (tag.trim() === '') return;
              addTag.mutate(tag);
              form.reset();
            }}
          >
            <input name="tag" aria-label="Add tag" placeholder="+ tag" style={{ padding: 'var(--space-1) var(--space-2)' }} />
          </form>
        </div>
      </Card>

      {isOwner ? (
        <Card title="Communications">
          {communications.isLoading ? (
            <Skeleton shape="text" lines={4} />
          ) : (
            <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {(communications.data?.items ?? []).map((entry) => (
                <li key={entry.comm_id} style={{ padding: 'var(--space-1) 0', color: 'var(--text-muted)' }}>
                  {entry.sent_at.slice(0, 16).replace('T', ' ')} · {entry.channel} · {entry.template ?? 'free text'} ·{' '}
                  <Pill tone={entry.delivery_status === 'failed' ? 'crit' : entry.delivery_status === 'queued' ? 'warn' : 'ok'}>
                    {entry.delivery_status ?? 'unknown'}
                  </Pill>
                </li>
              ))}
              {(communications.data?.items ?? []).length === 0 ? (
                <li style={{ color: 'var(--text-faint)' }}>Nothing sent yet.</li>
              ) : null}
            </ol>
          )}
        </Card>
      ) : null}

      <ConfirmDialog
        open={creditOpen}
        title={isOwner ? 'Issue Research Rewards credit' : 'Request a Research Rewards credit'}
        body={
          <label style={{ display: 'block' }}>
            <span className="caps-label">Amount in cents</span>
            <input
              type="number"
              min={1}
              max={50_000}
              value={creditCents}
              aria-label="Credit amount in cents"
              onChange={(event) => setCreditCents(Number(event.currentTarget.value))}
              style={{ display: 'block', width: '100%', padding: 'var(--space-2)' }}
            />
          </label>
        }
        effects={
          isOwner
            ? ['Writes a ledger entry through the bridge. Balances are never set directly.', 'Capped at $100 per action and $500 per day across the console.']
            : ['Creates an owner approval item. Nothing moves until the owner approves it.']
        }
        confirmLabel={isOwner ? 'Issue credit' : 'Send request'}
        reasonRequired
        busy={issueCredit.isPending || requestCredit.isPending}
        onCancel={() => setCreditOpen(false)}
        onConfirm={(reason) => {
          if (isOwner) {
            issueCredit.mutate({ delta_cents: creditCents, reason, note: null });
          } else {
            requestCredit.mutate({ delta_cents: creditCents, reason });
          }
          setCreditOpen(false);
        }}
      />

      {messageOpen ? (
        <SendMessageDialog customerKey={customerKey} channel={messageOpen} onClose={() => setMessageOpen(null)} />
      ) : null}
    </div>
  );
}
